import moment from 'moment';
import qs from 'query-string';

import { formatDateForSearchUrl } from './url';
const DEPART_DIRECTION = 'depart';
const RETURN_DIRECTION = 'return';

// How many calendar days into the past a depart date may be and still validate on the
// server. UTC leads every timezone west of it by at most one calendar day (down to
// UTC-12), and westward is the only direction this fires -- users at or east of UTC have
// a local "today" that is same-or-ahead of the server's, so they already validate
// correctly. One day is therefore the exact window needed to stop an evening same-day
// search (e.g. 11pm ET, when the servers' UTC clock has already rolled over) being
// silently redirected to tomorrow, without widening stale-date acceptance any further.
// The client stays strict against the browser's local clock and corrects anything
// genuinely stale that this lets through. See .docs/knowledge/search/depart-date-validation.md.
const SERVER_PAST_DAY_ALLOWANCE = 1;

function directionIsValidOnServer(direction, departTripId) {
    if (direction === DEPART_DIRECTION) {
        return true;
    }

    return !!departTripId;
}

function directionIsValid(direction, returnDate) {
    if (direction === RETURN_DIRECTION) {
        return !!returnDate;
    }

    return direction === DEPART_DIRECTION;
}

function _departDateIsValid(departDate, pastDayAllowance = 0) {
    if (!departDate) {
        return false;
    }
    const date = moment(departDate);
    const earliestValidDate = moment().subtract(pastDayAllowance, 'days');

    return date.isValid() && date.isSameOrAfter(earliestValidDate, 'day');
}

function _datePairIsValid(departDate, returnDate) {
    if (!returnDate) {
        return true;
    }

    const depart = moment(departDate);
    const ret = moment(returnDate);

    return depart.isValid()
        && ret.isValid()
        && depart.isSameOrBefore(ret, 'day');
}

function urlIsValidOnServer(params) {
    if (!directionIsValidOnServer(params.direction, params.departTripId)) {
        return false;
    }

    return urlIsValid(params, SERVER_PAST_DAY_ALLOWANCE);
}

function urlIsValid(params, pastDayAllowance = 0) {
    let {
        direction,
        departDate,
        returnDate,
        originText,
        originPlaceId,
        destinationText,
        destinationPlaceId,
        urlDestinationText,
        urlOriginText,
        urlOriginPlaceId,
        urlDestinationPlaceId,
    } = params;

    urlOriginText = decodeURIComponent(urlOriginText);
    originText = originText
        ? decodeURIComponent(originText)
        : undefined;
    urlDestinationText = decodeURIComponent(urlDestinationText);
    destinationText = destinationText
        ? decodeURIComponent(destinationText)
        : undefined;

    if (destinationText && originText) {
        const originMatches = urlOriginText === originText;
        const destinationMatches = urlDestinationText === destinationText;
        if (!originMatches || !destinationMatches) {
            return false;
        }
    }

    if (originPlaceId && destinationPlaceId) {
        const originMatches = urlOriginPlaceId === originPlaceId;
        const destinationMatches = urlDestinationPlaceId === destinationPlaceId;
        if (!originMatches || !destinationMatches) {
            return false;
        }
    }

    if (!directionIsValid(direction, returnDate)) {
        return false;
    }

    return _departDateIsValid(departDate, pastDayAllowance)
        && _datePairIsValid(departDate, returnDate);
}

function buildUrlParams(urlSearchParams, searchParams = {}) {
    return {
        direction: urlSearchParams.direction,
        departDate: urlSearchParams.departDate,
        returnDate: urlSearchParams.returnDate,
        originText: searchParams.originText || urlSearchParams.originText,
        passengers: searchParams.passengers || urlSearchParams.passengers,
        destinationText: searchParams.destinationText || urlSearchParams.destinationText,
        originPlaceId: searchParams.originPlaceId || urlSearchParams.originPlaceId,
        destinationPlaceId: searchParams.destinationPlaceId || urlSearchParams.destinationPlaceId,
        departTripId: searchParams.departTripId || urlSearchParams.departTripId,
        urlOriginText: urlSearchParams.originText,
        urlOriginPlaceId: urlSearchParams.originPlaceId,
        urlPassengers: urlSearchParams.passengers,
        urlDestinationText: urlSearchParams.destinationText,
        urlDestinationPlaceId: urlSearchParams.destinationPlaceId,
        urlDepartTripId: urlSearchParams.departTripId,
    };
}

function buildValidServerUrl(params) {
    const serverParams = params.direction === RETURN_DIRECTION
        ? { ...params, direction: DEPART_DIRECTION }
        : params;

    return buildValidUrl(serverParams, {}, SERVER_PAST_DAY_ALLOWANCE);
}

function buildValidUrl(params, urlFilterParams = {}, pastDayAllowance = 0) {
    const {
        direction,
        originText,
        originPlaceId,
        destinationText,
        destinationPlaceId,
        departDate,
        returnDate,
        departTripId,
        passengers,
    } = params;

    const invalidOrigin = !originText && !originPlaceId;
    const invalidDestination = !destinationText && !destinationPlaceId;
    if (invalidOrigin || invalidDestination) {
        return undefined;
    }

    const directionToSet = directionIsValid(direction, returnDate)
        ? direction
        : DEPART_DIRECTION;
    const today = moment().format('YYYY-MM-DD');
    const validDepartDate = _departDateIsValid(departDate, pastDayAllowance)
        ? departDate
        : today;
    const validReturnDate = _datePairIsValid(validDepartDate, returnDate)
        ? returnDate
        : validDepartDate;

    const urlComps = [
        directionToSet,
        originText,
        destinationText,
        validDepartDate,
        validReturnDate,
    ];
    const fullPath = urlComps.reduce((path, urlComp) => {
        if (!urlComp) {
            return path;
        }
        return `${path}/${encodeURIComponent(urlComp)}`;
    }, '');

    let queries = {
        opid: originPlaceId,
        dpid: destinationPlaceId,
        p: passengers,
        ...urlFilterParams,
    };

    if (direction === RETURN_DIRECTION) {
        queries.dtid = departTripId;
    }

    return `${fullPath}/?${qs.stringify(queries)}`;
}

function getItineraryId(outboundTripIds, returnTripIds = []) {
    let itineraryId = outboundTripIds.join('|');
    if (returnTripIds.length) {
        itineraryId = itineraryId.concat('||').concat(returnTripIds.join('|'));
    }
    return itineraryId;
}

function buildSearchUrl(opts, urlFilterParams) {
    const {
        direction,
        departDate,
        returnDate,
        originText,
        destinationText,
        originPlaceId,
        destinationPlaceId,
        departTripId,
        passengers,
    } = opts;

    let urlParams = null;

    const params = {
        direction,
        departDate: formatDateForSearchUrl(departDate),
        returnDate: returnDate
            ? formatDateForSearchUrl(returnDate)
            : null,
        originText,
        destinationText,
        originPlaceId,
        passengers,
        destinationPlaceId,
        departTripId,
    };

    urlParams = buildUrlParams(params);

    return buildValidUrl(urlParams, urlFilterParams);
}

export {
    buildSearchUrl,
    buildUrlParams,
    buildValidServerUrl,
    buildValidUrl,
    directionIsValid,
    directionIsValidOnServer,
    getItineraryId,
    urlIsValid,
    urlIsValidOnServer,
};
